import React from "react";
import { ResponsiveLine } from "@nivo/line";
import { Row, Col, Card } from "antd";
import Localize from "../../../libs/localization";

const SI_SYMBOL = ["€", "k€", "M€", "B€", "T€", "P€", "E€"];

function abbreviateNumber(number) {
  if (number === 0) return "0€";
  var tier = (Math.log10(Math.abs(number)) / 3) | 0;
  if (tier < 0) tier = 0;
  var suffix = SI_SYMBOL[tier] || "€";
  var scale = Math.pow(10, tier * 3);
  var scaled = number / scale;
  return scaled.toFixed(1).replace(".", ",") + suffix;
}

const CATEGORY10_COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
];

const ChartWrapper = ({ title, data, yFormatAbbreviate = true }) => {
  // Map colors manually so legend and chart are in sync
  const dataWithColors = data.map((d, i) => ({
    ...d,
    color: CATEGORY10_COLORS[i % CATEGORY10_COLORS.length],
  }));

  return (
    <Card style={{ marginBottom: 20 }}>
      <h3 style={{ fontWeight: "bold", marginBottom: 20 }}>{title}</h3>
      <div style={{ height: "350px", width: "100%" }}>
        <ResponsiveLine
          data={dataWithColors}
          margin={{ top: 10, right: 30, bottom: 50, left: 80 }}
          xScale={{ type: "linear", min: "auto", max: "auto" }}
          yScale={{ type: "linear", min: "auto", max: "auto" }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            orient: "bottom",
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: Localize("COMMON.YEAR"),
            legendOffset: 36,
            legendPosition: "middle",
            format: (d) => (Number.isInteger(d) ? d.toString() : ""),
          }}
          yFormat={yFormatAbbreviate ? (e) => abbreviateNumber(e) : undefined}
          axisLeft={{
            orient: "left",
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legendPosition: "middle",
            format: yFormatAbbreviate
              ? (e) => abbreviateNumber(e)
              : (e) => (Math.floor(e) === e ? e : ""),
          }}
          useMesh={true}
          colors={(d) => d.color}
          pointSize={10}
          pointColor={{ theme: "background" }}
          pointBorderWidth={2}
          pointBorderColor={{ from: "serieColor" }}
          pointLabelYOffset={-12}
          tooltip={({ point }) => {
            return (
              <div
                style={{
                  background: "white",
                  padding: "9px 12px",
                  border: "1px solid #eee",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: point.serieColor,
                    borderRadius: "50%",
                  }}
                />
                <div style={{ fontSize: "0.9rem", color: "#333" }}>
                  <strong>{point.serieId}</strong>
                  <div style={{ color: "#666", fontSize: "0.8rem" }}>
                    {point.data.yFormatted} ({point.data.xFormatted})
                  </div>
                </div>
              </div>
            );
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "12px",
          marginTop: "10px",
          padding: "0 10px",
        }}
      >
        {dataWithColors.map((d) => (
          <div
            key={d.id}
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: "0.85rem",
              color: "#888",
              fontWeight: "500",
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: d.color,
                marginRight: 6,
              }}
            />
            {d.id}
          </div>
        ))}
      </div>
    </Card>
  );
};

const DashboardComparisonGraphs = ({ companies }) => {
  if (!companies || companies.length === 0) return null;

  const validCompanies = companies.filter(
    (c) => c.data && c.data.financial_history,
  );
  if (validCompanies.length === 0) return null;

  // Prepare datasets
  const revenueSeries = [];
  const incomeSeries = [];
  const employeesSeries = [];

  validCompanies.forEach((company) => {
    const history = company.data.financial_history;
    const name = company.data.name;

    const revData = [];
    const incData = [];
    const empData = [];

    // Keys are years, we must ensure they are numeric for the linear scale
    const years = Object.keys(history).sort(
      (a, b) => parseInt(a) - parseInt(b),
    );

    years.forEach((yearStr) => {
      const year = parseInt(yearStr);
      if (history[yearStr].revenue !== undefined) {
        revData.push({ x: year, y: history[yearStr].revenue || 0 });
      }
      if (history[yearStr].operatingResult !== undefined) {
        incData.push({ x: year, y: history[yearStr].operatingResult || 0 });
      }
      if (history[yearStr].employees !== undefined) {
        empData.push({ x: year, y: history[yearStr].employees || 0 });
      }
    });

    if (revData.length > 0) revenueSeries.push({ id: name, data: revData });
    if (incData.length > 0) incomeSeries.push({ id: name, data: incData });
    if (empData.length > 0) employeesSeries.push({ id: name, data: empData });
  });

  return (
    <div style={{ marginTop: 40 }}>
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12} lg={8} style={{ minWidth: 300, flex: 1 }}>
          <ChartWrapper
            title={Localize("COMMON.REVENUE")}
            data={revenueSeries}
          />
        </Col>
        <Col xs={24} md={12} lg={8} style={{ minWidth: 300, flex: 1 }}>
          <ChartWrapper title={Localize("COMMON.INCOME")} data={incomeSeries} />
        </Col>
        <Col xs={24} md={12} lg={8} style={{ minWidth: 300, flex: 1 }}>
          <ChartWrapper
            title={Localize("COMMON.EMPLOYEES")}
            data={employeesSeries}
            yFormatAbbreviate={false}
          />
        </Col>
      </Row>
    </div>
  );
};

export default DashboardComparisonGraphs;
